import { ProjectType } from "../../../types/ProjectTypes";

export interface ReportTourStep {
    title: string;
    text: string;
    /** Highlights [data-tour-id="<anchorId>"] in the app DOM. */
    anchorId?: string;
    /** Highlights the first match of this selector inside the report iframe (same-origin only). */
    frameSelector?: string;
}

export const ATTACHMENTS_TOUR_ID = "report-attachments";
export const ASK_ASSISTANT_TOUR_ID = "report-ask-assistant";

const ASK_ASSISTANT_STEP: ReportTourStep = {
    title: "Questions? Just ask",
    text: "The assistant has read this report end to end. Click this button any time to ask about the analysis, dig into a number, or get help deciding what to do next.",
    anchorId: ASK_ASSISTANT_TOUR_ID,
};

const DEAL_STEPS: ReportTourStep[] = [
    {
        title: "Welcome to your Deal Analysis.",
        text: "Your negotiation playbook is ready. Here's a quick tour of the report so you know where everything lives. It takes less than a minute.",
    },
    {
        title: "The headline, up front",
        text: "These cards give you the verdict at a glance: our recommendation, the savings opportunity we identified, and the overall contract score. They stay pinned as you scroll.",
        frameSelector: ".report-summary-container",
    },
    {
        title: "Three views of the deal",
        text: "Switch between How to Execute (the negotiation plan), Detailed Analysis (the full supporting evidence), and About the Deal (the context and commercial terms).",
        frameSelector: ".report-utility-nav-container",
    },
    {
        title: "Jump straight to a section",
        text: "Each view has its own table of contents. Click any section to scroll directly to it.",
        frameSelector: ".report-sidenav-container",
    },
    {
        title: "The full report",
        text: "The main block of the report lives here: negotiation levers, pricing benchmarks, suggested contract language, and more. Scroll to read it top to bottom.",
        frameSelector: ".report-container",
    },
    {
        title: "Your documents, one click away",
        text: "Any potential supporting documents and appendices are available here in the Attachments dropdown, whenever you need them.",
        anchorId: ATTACHMENTS_TOUR_ID,
    },
];

const RFX_STEPS: ReportTourStep[] = [
    {
        title: "Welcome to your RFx Analysis",
        text: "Your award recommendation is ready. Here's a quick tour of the report so you know where everything lives. It takes less than a minute.",
    },
    {
        title: "The headline, up front",
        text: "These cards give you the verdict at a glance: our award recommendation, the savings against your budget, and the key risks to watch. They stay pinned as you scroll.",
        frameSelector: ".report-summary-container",
    },
    {
        title: "Jump straight to a section",
        text: "The table of contents covers the whole analysis — from the commercial overview and vendor-by-vendor evaluation to the recommended award structure. Click any section to scroll directly to it.",
        frameSelector: ".report-sidenav-container",
    },
    {
        title: "The full report",
        text: "The main block of the report lives here: how each vendor was evaluated, the cost structure behind the numbers, and the savings and negotiation levers we identified. Scroll to read it top to bottom.",
        frameSelector: ".report-container",
    },
    {
        title: "Your documents, one click away",
        text: "Any potential supporting documents and appendices are available here in the Attachments dropdown, whenever you need them.",
        anchorId: ATTACHMENTS_TOUR_ID,
    },
];

export function getReportTourSteps(
    projectType: ProjectType,
    options: { includeAskAssistant: boolean }
): ReportTourStep[] {
    // The attachments step is always included: while the tour is open, FlexibleOutput
    // forces the attachments dropdown to render even when the report has none.
    const steps = projectType === ProjectType.RFX ? RFX_STEPS : DEAL_STEPS;
    // The assistant step only makes sense when the user actually has the ask
    // button (feature-gated per org/user).
    return options.includeAskAssistant ? [...steps, ASK_ASSISTANT_STEP] : steps;
}
